import { StaffTicketDetail, TicketCommentKind, TicketStatus } from "@signalist/shared-types";

import { isStaffAuthor } from "@/pages/Private/pages/Support/pages/TicketDetail/utils/ticketThread";

const MINUTE_MS = 60 * 1000;

/**
 * Minutes from the ticket's creation to Signalist's first public reply — the rule behind the
 * queue's median (PREC-3939 decision 2): the first non-internal MESSAGE whose author belongs to
 * another company, on a ticket not filed by staff. Null until such a reply exists; a staff-filed
 * ticket has none by definition (its description is staff-authored).
 */
export const firstReplyMinutes = (ticket: StaffTicketDetail): number | null => {
	if (ticket.filedByStaffUserId !== null) {
		return null;
	}

	const reply = ticket.comments.find(
		(comment) =>
			comment.kind === TicketCommentKind.MESSAGE &&
			!comment.isInternal &&
			isStaffAuthor(ticket, comment.author)
	);

	if (!reply) {
		return null;
	}

	return Math.max(
		0,
		Math.round((Date.parse(reply.createdAt) - Date.parse(ticket.createdAt)) / MINUTE_MS)
	);
};

export interface WaitingSince {
	at: string;
	/** True while the ball is in Signalist's court — the rail paints the date red. */
	onUs: boolean;
}

/**
 * Since when the current side has been waiting (decision 6): the customer's last activity while
 * the ticket waits on us (OPEN counts — nobody has answered yet), our last activity while it waits
 * on the customer, nothing once resolved or closed.
 */
export const waitingSince = (ticket: StaffTicketDetail): WaitingSince | null => {
	switch (ticket.status) {
		case TicketStatus.OPEN:
		case TicketStatus.WAITING_ON_US:
			return { at: ticket.lastCustomerActivityAt ?? ticket.createdAt, onUs: true };
		case TicketStatus.WAITING_ON_CUSTOMER:
			return ticket.lastStaffActivityAt ? { at: ticket.lastStaffActivityAt, onUs: false } : null;
		default:
			return null;
	}
};
